import React, { useState, useContext, useEffect } from "react";
import { CombinedContext } from "../../../context/combinedContext";
import "../../profileAdminCard/adminModals.css";
import "./staffInfoModal.css";
import companyBasedRequest from "../../../request/companyBasedRequest";
import translations from "../../../translations/translations";
import PhoneInput from "../../common/phoneInput/phoneInput";
import useModalDismiss from "../../common/useModalDismiss";

const BIO_MAX = 300;

const StaffInfoModal = ({ open, onClose, staff, setStaff }) => {
    useModalDismiss(onClose, { enabled: open });
    const { language, userData, setUserData } = useContext(CombinedContext);

    const [local, setLocal] = useState({
        firstName: "",
        lastName: "",
        bio: "",
        phone: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!staff) return;
        setLocal({
            firstName: staff.firstName || "",
            lastName: staff.lastName || "",
            bio: staff.bio || "",
            phone: staff.phone || "",
        });
    }, [staff]);

    if (!open) return null;

    const t = translations[language].profileStaff.staffInfo;

    const handleSave = async () => {
        const cardData = {
            firstName: local.firstName,
            lastName: local.lastName,
            bio: local.bio,
            phone: local.phone,
        };
        const apiData = {
            firstName: local.firstName,
            lastName: local.lastName,
            bio: local.bio,
            phoneNumber: local.phone,
        };

        // The account page reads `user.phone` while this card reads
        // `user.phoneNumber`, so both have to move together or one of the two
        // keeps showing the old number until a reload.
        const applyToUser = (patch) => {
            setUserData((prev) => ({
                ...prev,
                user: { ...prev.user, ...patch, phone: patch.phoneNumber, phoneNumber: patch.phoneNumber },
            }));
        };

        setStaff((prev) => ({ ...prev, ...cardData }));
        applyToUser(apiData);

        try {
            setSaving(true);
            const res = await companyBasedRequest.updateStaffInfo(
                staff.userId,
                apiData,
                userData.cognitoTokens.AccessToken
            );

            // Reconcile the optimistic patch with whatever the route stored.
            if (res?.user) {
                const saved = res.user;
                const phoneNumber = saved.phoneNumber ?? saved.phone ?? local.phone;

                setStaff((prev) => ({
                    ...prev,
                    firstName: saved.firstName ?? prev.firstName,
                    lastName: saved.lastName ?? prev.lastName,
                    bio: saved.bio ?? prev.bio,
                    phone: phoneNumber,
                }));
                applyToUser({
                    firstName: saved.firstName ?? local.firstName,
                    lastName: saved.lastName ?? local.lastName,
                    bio: saved.bio ?? local.bio,
                    phoneNumber,
                });
            }

            onClose();
        } catch (err) {
            console.error("Failed to update staff info", err);
        } finally {
            setSaving(false);
        }
    };

    const bioCount = local.bio.length;

    return (
        <div className="sf-pm-overlay">
            <div className="sf-pm-modal is-compact">
                {/* HEADER */}
                <div className="sf-pm-header">
                    <div className="sf-pm-heading">
                        <h2 className="sf-pm-title">{t.title}</h2>
                        <p className="sf-pm-sub">{t.intro}</p>
                    </div>
                    <button
                        className="sf-pm-close"
                        onClick={onClose}
                        aria-label={translations[language].calendar.editor.cancel}
                    >
                        ✕
                    </button>
                </div>

                {/* BODY */}
                <div className="sf-pm-body">
                    <section className="sf-pm-section">
                        <p className="sf-pm-label">{t.title}</p>

                        <div className="sf-pm-card">
                            <div className="sf-pm-grid-2">
                                <div className="sf-pm-field">
                                    <label className="sf-pm-field-label" htmlFor="sf-pm-pstaff-first">
                                        {t.firstName}
                                    </label>
                                    <input
                                        id="sf-pm-pstaff-first"
                                        className="sf-pm-input"
                                        value={local.firstName}
                                        onChange={(e) => setLocal({ ...local, firstName: e.target.value })}
                                    />
                                </div>

                                <div className="sf-pm-field">
                                    <label className="sf-pm-field-label" htmlFor="sf-pm-pstaff-last">
                                        {t.lastName}
                                    </label>
                                    <input
                                        id="sf-pm-pstaff-last"
                                        className="sf-pm-input"
                                        value={local.lastName}
                                        onChange={(e) => setLocal({ ...local, lastName: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="sf-pm-field">
                                <label className="sf-pm-field-label">{t.phone}</label>
                                <PhoneInput
                                    businessPhone={local.phone}
                                    setBusinessPhone={(value) => setLocal((prev) => ({ ...prev, phone: value }))}
                                />
                            </div>

                            <div className="sf-pm-field">
                                <label className="sf-pm-field-label" htmlFor="sf-pm-pstaff-bio">
                                    {t.bio}
                                </label>
                                {/* Fixed height with a counter, instead of the auto-growing
                                    textarea this used to be — that one pushed the footer off
                                    a short screen as you typed. */}
                                <div className="sf-pm-counted">
                                    <textarea
                                        id="sf-pm-pstaff-bio"
                                        className="sf-pm-textarea"
                                        value={local.bio}
                                        maxLength={BIO_MAX}
                                        onChange={(e) => setLocal({ ...local, bio: e.target.value })}
                                    />
                                    <span className={`sf-pm-counter${bioCount >= BIO_MAX - 30 ? " is-warn" : ""}`}>
                                        {bioCount}/{BIO_MAX}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                {/* FOOTER */}
                <div className="sf-pm-footer">
                    <button className="sf-pm-btn is-ghost" onClick={onClose} disabled={saving}>
                        {translations[language].calendar.editor.cancel}
                    </button>
                    <button className="sf-pm-btn is-primary" onClick={handleSave} disabled={saving}>
                        {saving && <span className="sf-pm-spinner" aria-hidden="true" />}
                        {saving
                            ? translations[language].calendar.editor.saving
                            : translations[language].calendar.editor.save}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StaffInfoModal;
